import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { GithubIcon } from '@/components/icons/BrandIcons';
import { useGitHubLogin } from '@/hooks/useGitHubLogin';

export default function StickyCtaBar() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const finalCtaVisibleRef = useRef(false);
  const { isLoading, handleGitHubLogin } = useGitHubLogin();

  useEffect(() => {
    const videoSection = document.getElementById('video-demo-section');
    const finalCta = document.getElementById('final-cta');
    const observers: IntersectionObserver[] = [];
    const videoPastRef = { current: false };

    // Show sticky bar only after user scrolls past the video section
    if (videoSection) {
      const videoObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting && entry.boundingClientRect.bottom < 0) {
            videoPastRef.current = true;
            if (!finalCtaVisibleRef.current && !isDismissed) {
              setIsVisible(true);
            }
          } else if (entry.isIntersecting) {
            videoPastRef.current = false;
            setIsVisible(false);
          }
        },
        { threshold: 0 }
      );
      videoObserver.observe(videoSection);
      observers.push(videoObserver);
    } else {
      // Fallback: if video section not found, use scroll threshold
      const handleScroll = () => {
        const shouldShow = window.scrollY > window.innerHeight * 1.5;
        setIsVisible(shouldShow && !finalCtaVisibleRef.current && !isDismissed);
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }

    // Hide sticky bar when final CTA section enters viewport
    if (finalCta) {
      const ctaObserver = new IntersectionObserver(
        ([entry]) => {
          finalCtaVisibleRef.current = entry.isIntersecting;
          if (entry.isIntersecting) {
            setIsVisible(false);
          } else if (videoPastRef.current && !isDismissed) {
            setIsVisible(true);
          }
        },
        { threshold: 0.1, rootMargin: '0px 0px 100px 0px' }
      );
      ctaObserver.observe(finalCta);
      observers.push(ctaObserver);
    }

    return () => observers.forEach(o => o.disconnect());
  }, [isDismissed]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg transform transition-transform duration-300 animate-slide-up">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Value prop */}
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Find your biggest bottleneck in 5 minutes
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              No credit card required • Read-only access
            </p>
          </div>

          {/* Mobile: Simplified */}
          <p className="sm:hidden text-sm font-medium text-gray-900 dark:text-white">
            Find your biggest bottleneck
          </p>

          {/* Right: CTA + Dismiss */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => handleGitHubLogin('sticky_bar')}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-primary-600 text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              <GithubIcon className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{isLoading ? 'Connecting...' : 'Find Your #1 Bottleneck'}</span>
              <span className="sm:hidden">{isLoading ? '...' : 'Find Bottleneck'}</span>
            </button>
            <button
              onClick={handleDismiss}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
